import { useState, useRef, useEffect } from "react";
import { addToWishlist, removeFromWishlist as apiRemoveFromWishlist, sendNotification, recordPlanetVisit } from "../api/users";
import { calcSimilarity } from "../utils/similarity";
import { getUserDisplayName } from "../utils/format";
import UserBooksView from "../components/UserBooksView";
import BookOrbitTrail from "../components/BookOrbitTrail";
import YearTrailModal from "../modals/YearTrailModal";
import UserQuotes from "./UserQuotes";
import { collectYears, MIN_POINTS } from "../utils/renderYearTrail";
import MiniBook from "../components/MiniBook";
import ProfileHero from "./ProfileHero";
import ExportCsvButton from "../components/ExportCsvButton";
import { booksToCsv, csvFileName } from "../utils/booksCsv";
import DeleteAccountModal from "./DeleteAccountModal";
import "./UserScreen.css";
import { readBooks, wishBooks } from "../utils/bookState";

const UserScreen = ({ userId, openTab, clearOpenTab, focusId, clearFocus, onBack, onBookClick, myBooks, allBooks = [], users, currentUser, onUserUpdated, myWishlist = [], myFriends = [], reloadWishlist }) => {
  const isMe = userId === "me" || userId === currentUser?.uid;
  const [viewType, setViewType] = useState(isMe ? "orbit" : "shared");
  const [trailYear, setTrailYear] = useState(null);

  const tabsRef = useRef(null);
  /* 탭 줄로 데려가 달라는 요청. 숫자를 올려 이펙트를 다시 돌린다. */
  const [scrollReq, setScrollReq] = useState(0);

  // 다른 화면에서 특정 탭을 지정해 들어올 수 있다. 한 번 쓰고 비운다 —
  // 남겨두면 그 뒤로 탭을 눌러도 여기로 되돌아온다.
  useEffect(() => {
    if (!openTab) return;
    setViewType(openTab);
    /* 짚을 문장이 있으면 그 문장으로 가는 스크롤을 UserQuotes 가 한다.
       여기서 탭 줄로도 끌면 둘이 서로 덮어써, 늦게 도는 쪽이 이긴다. */
    if (!focusId) setScrollReq((n) => n + 1);
    clearOpenTab?.();
  }, [openTab, clearOpenTab, focusId]);

  /* 탭만 골라두면 화면은 행성 맨 위에 있어서 보러 온 것이 접혀 있는 것처럼
     보인다. 탭 줄까지 함께 데려간다.
     이것을 위 이펙트 안에서 하면 안 된다 — clearOpenTab() 이 곧바로
     openTab 을 비워 이펙트가 다시 돌고, 그 정리 과정에서 예약해 둔
     스크롤이 취소된다(그래서 scrollY 가 0 그대로였다).
     표지가 자리를 잡으며 위쪽 높이가 바뀌므로 몇 번 더 맞춘다. */
  useEffect(() => {
    if (!scrollReq) return;
    /* scrollIntoView({block:"start"}) 를 쓰면 안 된다. 탭 줄이 스스로
       overflow-x 컨테이너라 브라우저가 "nearest" 처럼 몇 px 만 밀고
       끝냈다 — 재보니 4px 이었다. 자리를 직접 계산한다.
       띄울 높이는 CSS 의 scroll-margin-top 을 읽어 쓴다(sticky 인
       back-bar 높이). 두 곳에 같은 숫자를 적지 않는다. */
    const bring = () => {
      const el = tabsRef.current;
      if (!el) return;
      const pad = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
      window.scrollTo(0, Math.max(0, window.scrollY + el.getBoundingClientRect().top - pad));

      /* 탭 줄도 옆으로 맞춘다. 375px 에서 이 줄은 229px 이 화면 밖으로
         밀려 있어서, 세로만 맞추면 '월별' 로 들어왔는데 보이는 것은
         궤적·문장·장르별·작가별 이고 금색 밑줄만 오른쪽 끝에 걸린다.
         고른 탭을 가운데로 가져온다. */
      const tab = el.querySelector(".view-tab.active");
      if (!tab) return;
      const box = el.getBoundingClientRect();
      const now = tab.getBoundingClientRect();
      el.scrollLeft += now.left - box.left - (box.width - now.width) / 2;
    };
    bring();
    let raf = requestAnimationFrame(() => {
      bring();
      raf = requestAnimationFrame(bring);
    });
    const late = setTimeout(bring, 340);
    return () => { cancelAnimationFrame(raf); clearTimeout(late); };
  }, [scrollReq]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // 담아두기만 한 책은 읽은 책이 아니다. 권수·유사도·장르별·궤적에서 모두 빼둔다.
  const myReadBooks = readBooks(myBooks, currentUser?.uid);

  const myUser = (users || []).find(u => u.id === currentUser?.uid) || {
    id: currentUser?.uid || "me",
    name: getUserDisplayName(currentUser),
    handle: currentUser?.email?.split("@")[0] || "reader",
    avatar: "🌿",
    bookIds: myReadBooks.map(b => b.id),
  };

  const user = isMe ? myUser : (users || []).find(r => r.id === userId);

  // 남의 행성에 들른 것을 남긴다. 게스트는 매번 다른 임시 계정이라
  // 같은 사람인지 알 수 없어 세지 않는다.
  useEffect(() => {
    if (isMe || !user?.id || !currentUser?.uid || currentUser.isAnonymous) return;
    recordPlanetVisit(user.id, currentUser.uid);
  }, [isMe, user?.id, currentUser?.uid, currentUser?.isAnonymous]);
  if (!user) return null;

  const myBookIds = myReadBooks.map(b => b.id);
  const userBooks = isMe
    ? myReadBooks
    : (user.bookIds || []).map(bId => {
        return myBooks.find(b => b.id === bId) || allBooks.find(b => b.id === bId);
      }).filter(Boolean);

  const sharedBooks = isMe ? [] : userBooks.filter(b => myBookIds.includes(b.id));
  const similarity = isMe ? null : calcSimilarity(myBookIds, user.bookIds || []);
  const isFriend = !isMe && similarity >= 30;
  const sharedBookIds = sharedBooks.map(b => b.id);

  const isInWishlist = (bookId) => myWishlist.some(w => w.bookId === bookId);

  const toggleWishlist = async (book) => {
    if (!currentUser || currentUser.isAnonymous) return;
    const existing = myWishlist.find(w => w.bookId === book.id);

    if (existing) {
      await apiRemoveFromWishlist(currentUser.uid, book.id);
    } else {
      // 책 제목/저자는 wishlist 조회 시 books 테이블에서 join 해 오므로 따로 복사하지 않는다
      await addToWishlist(currentUser.uid, book.id, user.id);
      await sendNotification({
        toUserId: user.id,
        fromUserId: currentUser.uid,
        type: "wishlist",
        bookTitle: book.title,
      });
    }
    if (reloadWishlist) reloadWishlist();
  };

  const removeFromWishlist = async (item) => {
    if (!currentUser || currentUser.isAnonymous) return;
    await apiRemoveFromWishlist(currentUser.uid, item.bookId);
    if (reloadWishlist) reloadWishlist();
  };

  return (
    <div className={`bp-layer bp-layer-full${isMe ? " planet-enter" : ""}`}>
      <div className="back-bar" onClick={onBack}>← <span>돌아가기</span></div>

      {!isMe && sharedBooks.length > 0 && (
        <div className="crossing-banner">
          <span className="crossing-banner-icon">✨</span>
          <div>
            <span className="crossing-banner-text">나와 </span>
            <span className="crossing-banner-count">{sharedBooks.length}권</span>
            <span className="crossing-banner-text">을 함께 읽었어요</span>
            {isFriend && <span className="friend-badge">🤝 이웃 행성</span>}
          </div>
        </div>
      )}

      <ProfileHero
        user={user}
        isMe={isMe}
        currentUser={currentUser}
        myBooks={myBooks}
        allBooks={allBooks}
        onGoQuotes={() => setViewType("quotes")}
        myUser={myUser}
        myFriends={myFriends}
        similarity={similarity}
        isFriend={isFriend}
        onUserUpdated={onUserUpdated}
        reloadWishlist={reloadWishlist}
        onShowDeleteModal={() => setShowDeleteModal(true)}
      />

      {/* 내 책을 표 파일로. 탭 줄 위에 두면 탭이 밀려 내려가 보인다. */}
      {/* 담아둔 책까지 함께 내보낸다 — '내 행성에 등록한 책' 은 그것까지다.
          userBooks 는 담아둔 책을 뺀 목록이라 여기서는 myBooks 를 쓴다. */}
      {isMe && !currentUser?.isAnonymous && (
        <ExportCsvButton
          build={() => booksToCsv(myBooks, user.id)}
          fileName={csvFileName()}
          count={myBooks.length}
          noun="권"
          label="엑셀로 내보내기"
          event="books_csv_export"
        />
      )}

      <div className="view-tabs" ref={tabsRef}>
        {isMe && (
          <button className={`view-tab${viewType === "orbit" ? " active" : ""}`} onClick={() => setViewType("orbit")}>
            🛸 궤적
          </button>
        )}
        {!isMe && sharedBooks.length > 0 && (
          <button className={`view-tab${viewType === "shared" ? " active" : ""}`} onClick={() => setViewType("shared")}>
            ✨ 공통
          </button>
        )}
        {!isMe && (
          <button className={`view-tab${viewType === "orbit" ? " active" : ""}`} onClick={() => setViewType("orbit")}>
            🛸 궤적
          </button>
        )}
        <button className={`view-tab${viewType === "quotes" ? " active" : ""}`} onClick={() => setViewType("quotes")}>
          ✍️ 문장
        </button>
        <button className={`view-tab${viewType === "month" ? " active" : ""}`} onClick={() => setViewType("month")}>
          월별
        </button>
        <button className={`view-tab${viewType === "wishlist" ? " active" : ""}`} onClick={() => setViewType("wishlist")}>
          🔖 읽고 싶은 책
        </button>
        {[["genre","장르별"],["author","작가별"]].map(([v, label]) => (
          <button key={v} className={`view-tab${viewType === v ? " active" : ""}`} onClick={() => setViewType(v)}>{label}</button>
        ))}
      </div>

      <div className="user-books-content">
        {viewType === "wishlist" ? (() => {
          /* 한 탭에 두 갈래를 둔다. 내가 담아둔 책(user_books.want_to_read)과
             남이 추천해 준 책(wishlist)은 출처만 다르고 둘 다 "아직 안 읽었고
             읽고 싶은 책" 이다. 탭을 따로 두면 어디에 담는 것인지 헷갈리고
             탭 줄도 넘친다. 출처 차이는 묶음 제목으로 드러낸다. */
          const mine = isMe
            ? wishBooks(myBooks, currentUser?.uid)
            : (user.wantBookIds || []).map(bId => allBooks.find(b => b.id === bId)).filter(Boolean);
          // 담아온 책은 wishlist 정책이 본인만 읽게 하므로 남의 행성에서는 비어 있다.
          const recommended = isMe ? myWishlist : [];
          return (
            <div>
              {mine.length === 0 && recommended.length === 0 ? (
                <div className="user-empty-text">
                  {isMe ? "아직 담아둔 책이 없어요 🔖" : "담아둔 책이 없어요 🔖"}
                </div>
              ) : (
                <>
                  {mine.length > 0 && (
                    <>
                      <div className="user-section-label">
                        🔖 담아둔 책 · {mine.length}권
                      </div>
                      <div className="genre-books">
                        {mine.map(b => (
                          <MiniBook key={b.id} book={b} onClick={onBookClick}
                            showWishlist={!currentUser?.isAnonymous} isWishlisted={isInWishlist(b.id)} onToggleWishlist={toggleWishlist} />
                        ))}
                      </div>
                    </>
                  )}
                  {recommended.length > 0 && (
                    <>
                      <div className="user-section-label">🪐 다른 행성에서 담아온 책 · {recommended.length}권</div>
                      <div className="wishlist-grid">
                        {recommended.map((item, i) => (
                          <div key={`${item.bookId}-${i}`} className="wishlist-card">
                            <div className="wishlist-card-info">
                              <div className="wishlist-card-title">{item.bookTitle}</div>
                              <div className="wishlist-card-author">{item.bookAuthor}</div>
                              <div className="wishlist-card-from">via @{item.fromUserName}</div>
                            </div>
                            {isMe && <button className="wishlist-card-remove" onClick={() => removeFromWishlist(item)}>✕</button>}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          );
        })() : viewType === "shared" && !isMe ? (
          <div>
            <div className="shared-section">
              <div className="shared-section-label">✨ 우리가 함께 읽은 책 · {sharedBooks.length}권</div>
              <div className="genre-books">
                {sharedBooks.map(b => (
                  <MiniBook key={b.id} book={b} onClick={onBookClick} isShared
                    isRecommended={!!(b.recommendedBy && b.recommendedBy[user.id])}
                    showWishlist={!currentUser?.isAnonymous} isWishlisted={isInWishlist(b.id)} onToggleWishlist={toggleWishlist} />
                ))}
              </div>
            </div>
            {(() => {
              const otherBooks = userBooks.filter(b => !myBookIds.includes(b.id));
              const otherCompleted = otherBooks.filter(b => b.completedBy && b.completedBy[user.id]);
              const otherReading = otherBooks.filter(b => !b.completedBy || !b.completedBy[user.id]);
              return (
                <>
                  {otherCompleted.length > 0 && (
                    <>
                      <div className="user-section-label user-section-label--teal">
                        ✅ 읽은 책 · {otherCompleted.length}권
                      </div>
                      <div className="genre-books">
                        {otherCompleted.map(b => (
                          <MiniBook key={b.id} book={b} onClick={onBookClick}
                            isRecommended={!!(b.recommendedBy && b.recommendedBy[user.id])}
                            showWishlist={!currentUser?.isAnonymous} isWishlisted={isInWishlist(b.id)} onToggleWishlist={toggleWishlist} />
                        ))}
                      </div>
                    </>
                  )}
                  {otherReading.length > 0 && (
                    <>
                      <div className="user-section-label user-section-label--orange">
                        📖 읽는 중 · {otherReading.length}권
                      </div>
                      <div className="genre-books">
                        {otherReading.map(b => (
                          <MiniBook key={b.id} book={b} onClick={onBookClick}
                            isRecommended={!!(b.recommendedBy && b.recommendedBy[user.id])}
                            showWishlist={!currentUser?.isAnonymous} isWishlisted={isInWishlist(b.id)} onToggleWishlist={toggleWishlist} />
                        ))}
                      </div>
                    </>
                  )}
                </>
              );
            })()}
          </div>
        ) : viewType === "quotes" ? (
          <UserQuotes user={user} isMe={isMe} currentUser={currentUser} onBookClick={onBookClick}
            focusId={focusId} clearFocus={clearFocus}
            onFocusMissed={() => setScrollReq((n) => n + 1)} />
        ) : viewType === "orbit" ? (
          <BookOrbitTrail
            books={userBooks}
            userId={user.id}
            onBookClick={onBookClick}
            /* 내 궤적만 내보낸다 — 남의 독서 기록을 이미지로 뽑아 돌리는 건 다른 이야기다 */
            onExport={isMe && !currentUser?.isAnonymous ? setTrailYear : null}
            exportableYears={collectYears(userBooks, user.id)
              .filter((y) => y.points.length >= MIN_POINTS)
              .map((y) => y.year)}
          />
        ) : (
          <UserBooksView books={userBooks} viewType={viewType} onBookClick={onBookClick} sharedBookIds={sharedBookIds}
            showWishlist={!isMe && !currentUser?.isAnonymous} isInWishlist={isInWishlist} onToggleWishlist={toggleWishlist} viewUserId={user.id}
            viewUserName={user.name} canExport={isMe && !currentUser?.isAnonymous} />
        )}
      </div>

      {trailYear && (
        <YearTrailModal
          books={userBooks}
          uid={user.id}
          name={user.name}
          year={trailYear}
          onClose={() => setTrailYear(null)}
        />
      )}

      {showDeleteModal && (
        <DeleteAccountModal
          currentUser={currentUser}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default UserScreen;
